import { PortalUserProfile } from '@amzn/awscat-pattern-library-service-model';
import { Mutex } from 'async-mutex';
import { jwtVerify, importJWK, JWTPayload } from 'jose';
import { DateTime } from 'luxon';
import PortalServiceClient from '../api/PortalServiceClient';
import { localLog } from '../loggers/Logger';
import GetJwksResponse from '../model/GetJwksResponse';
import AuthenticationInfo from './AuthenticationInfo';
import EnvironmentConfig from './EnvironmentConfig';

class AuthenticationManager {
  public static create() {
    if (!this.instance) {
      this.instance = new AuthenticationManager();
    }
    return this.instance;
  }
  private static instance: AuthenticationManager;
  private portalService: PortalServiceClient;
  // @ts-expect-error TS2564 - strict migration
  private currentToken: string;
  // @ts-expect-error TS2564 - strict migration
  private onSuccess: (info: AuthenticationInfo) => void;
  // @ts-expect-error TS2564 - strict migration
  private onError: (err: Error) => void;
  private mutex = new Mutex();
  // @ts-expect-error TS2564 - strict migration
  private authnInfo: AuthenticationInfo;

  get authInfo() {
    return this.authnInfo;
  }

  constructor() {
    this.portalService = PortalServiceClient.create();
  }

  public configure(
    onSuccess: (info: AuthenticationInfo) => Promise<void>,
    onError: (err: Error) => Promise<void>,
  ) {
    this.onSuccess = onSuccess;
    this.onError = onError;
  }

  public async renewAuthorInfo() {
    const release = await this.mutex.acquire();
    let authnInfo;
    try {
      authnInfo = await this.verifyToken(this.currentToken);
    } catch (e) {
      authnInfo = await this.refreshToken();
    } finally {
      release();
    }
    // @ts-expect-error TS2322 - strict migration
    this.authnInfo = authnInfo;
    return authnInfo;
  }

  // @ts-expect-error TS7030 - strict migration
  public async refreshToken() {
    try {
      localLog.debug('Refreshing token...');

      // Fetch the keys and profile alongside the token, because neither needs the token to start
      const [token, jwks, userProfile] = await Promise.all([
        this.portalService.getAuthenticationToken(),
        this.portalService.getJWKS(),
        this.portalService.getSignedInUserProfile(),
      ]);
      this.currentToken = token;

      const authorInfo = await this.verifyTokenWithKeys(token, jwks, userProfile);
      await this.completeSignIn(authorInfo);
      localLog.debug('Token refresh completed');
      return authorInfo;
    } catch (err) {
      // @ts-expect-error TS18046 - strict migration
      localLog.debug(`Failed to refresh token: ${JSON.stringify(err)} ${err.message}`);
      if (this.onError) {
        // @ts-expect-error TS2345 - strict migration
        await this.onError(err);
      }
    }
  }

  // @ts-expect-error TS7030 - strict migration
  public async processToken(token: string) {
    try {
      const authInfo = await this.verifyToken(token);
      await this.completeSignIn(authInfo);
      return authInfo;
    } catch (err) {
      // @ts-expect-error TS18046 - strict migration
      localLog.debug(`Failed to process token: ${JSON.stringify(err)} ${err.message}`);
      if (this.onError) {
        // @ts-expect-error TS2345 - strict migration
        await this.onError(err);
      }
    }
  }

  /**
   * Fetches the portal's keys and the user's profile, then verifies the token
   * with them.
   *
   * @throws When a fetch fails or the token fails verification.
   */
  private async verifyToken(token: string) {
    try {
      // Fetch the keys and profile together, because they don't depend on each other
      const [jwks, userProfile] = await Promise.all([
        this.portalService.getJWKS(),
        this.portalService.getSignedInUserProfile(),
      ]);

      return await this.verifyTokenWithKeys(token, jwks, userProfile);
    } catch (err) {
      localLog.error({ err }, 'Error while refreshing token.');
      throw err;
    }
  }

  /**
   * Verifies the token with keys the caller already fetched, then returns it
   * with the user's profile; makes no network calls.
   *
   * @throws When the token is invalid, expired, from the wrong issuer, or the
   *   keys contain no usable key.
   */
  private async verifyTokenWithKeys(
    token: string,
    jwks: GetJwksResponse,
    userProfile: PortalUserProfile,
  ): Promise<AuthenticationInfo> {
    let payload: JWTPayload;

    for (const key of jwks.keys) {
      const publicKey = await importJWK(key);
      const verifyResult = await jwtVerify(token, publicKey, {
        issuer: EnvironmentConfig.getConfig().tokenIssuer,
      });

      payload = verifyResult.payload;
      break;
    }

    return {
      tokenInfo: {
        // @ts-expect-error TS2454 - strict migration
        expiryDate: DateTime.fromMillis(payload.exp),
        token,
      },
      userProfile,
    };
  }

  /**
   * Stores the verified sign-in and tells the app, once the token has passed
   * its check.
   */
  private async completeSignIn(authInfo: AuthenticationInfo) {
    this.authnInfo = authInfo;
    if (this.onSuccess) {
      await this.onSuccess(authInfo);
    }
  }
}

export default AuthenticationManager;
